import React, { useState } from "react";
import {
  Table,
  Container,
  Row,
  Col,
  Spinner,
} from "react-bootstrap";
import CountryList from "../../CountryList";
import { googleApi } from "../../../services/api";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { FaChartLine, FaSearch } from "react-icons/fa";

// Registering necessary chart elements
ChartJS.register(
  LineElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const KeywordRelevanceComponent = () => {
  const [keyword, setKeyword] = useState("");
  const [country, setCountry] = useState("");
  const [appsData, setAppsData] = useState([]);
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch data based on keyword and country
  const fetchData = async () => {
    if (!keyword || !country) {
      alert("Please provide both keyword and country.");
      return;
    }

    setLoading(true);

    try {
      const { data: result } = await googleApi.get("keyword-relevance/", {
          params: { keyword, country },
        });
      const data = result?.apps ?? [];

      setAppsData(data);

      // Prepare data for the line chart
      const chartLabels = data.map((app) => app.app_name || "Unknown App");
      const chartScores = data.map((app) =>
        (app?.keywords ?? []).reduce(
          (acc, kw) => acc + (Number(kw?.score) || 0),
          0
        )
      );

      setChartData({
        labels: chartLabels,
        datasets: [
          {
            label: "Relevancy Score",
            data: chartScores,
            borderColor: "rgba(75, 192, 192, 1)",
            backgroundColor: "rgba(75, 192, 192, 0.2)",
            fill: true,
          },
        ],
        options: {
          responsive: true,
          plugins: {
            title: {
              display: true,
              text: "Relevancy Score Comparison (Line Chart)",
            },
          },
          scales: {
            x: {
              title: {
                display: true,
                text: "App Name",
              },
            },
            y: {
              title: {
                display: true,
                text: "Relevancy Score",
              },
            },
          },
        },
      });

    } catch (error) {
      console.error("Error fetching data", error);
      alert("Error fetching data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="mt-4">
       <div className="bg-white rounded-xl shadow-md p-6 mb-8 border border-gray-100">
        <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
          <FaChartLine className="mr-3 text-blue-500" />
          Keyword Relevancy
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
          <div className="md:col-span-5">
            <label className="block text-sm font-medium text-gray-700 mb-1">Enter Keyword</label>
            <input
              type="text"
              placeholder="Enter keyword"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              className="w-full bg-white border border-gray-300 rounded-md py-2 px-3 text-sm text-gray-700 placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
            />
          </div>
          <div className="md:col-span-5">
            <label className="block text-sm font-medium text-gray-700 mb-1">Select Country</label>
            <CountryList
              onSelect={(selectedCountry) => setCountry(selectedCountry)}
            />
          </div>
          <div className="md:col-span-2">
            <button
              onClick={fetchData}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md shadow transition duration-200 flex items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed"
              disabled={loading}
            >
               {loading ? (
                <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
              ) : (
                <FaSearch className="mr-2" />
              )}
              {loading ? "Loading..." : "Get Data"}
            </button>
          </div>
        </div>
      </div>

      {/* Loader Icon centered */}
      {loading && (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "300px" }}
        >
          <Spinner animation="border" role="status" />
          <span className="ms-2">Loading Please Wait...</span>
        </div>
      )}

      {/* Displaying data after loading */}
      {!loading && appsData.length > 0 && (
        <Row className="mt-4">
          <Col md={12} className="mt-4">
            {chartData && (
              <div
                style={{
                  backgroundColor: "rgba(75, 192, 192, 0.2)",
                  borderRadius: "10px",
                  padding: "20px",
                  marginBottom: "20px",
                }}
              >
                <h3>Relevancy Score Comparison (Line Chart)</h3>
                <Line data={chartData} options={chartData.options} />
              </div>
            )}
          </Col>

          <Col md={12} className="mt-4" style={{ height: "940px", overflowY: "auto" }}>
            <Table
            style={{
              fontSize: "0.8rem",
              borderRadius: "8px",
              border: "1px solid #ddd",
              boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
            }}
              striped
              bordered
              hover
              responsive
              className="mt-3 text-center table-custom"
              size="sm"
            >
              <thead>
                <tr>
                  <th style={{ width: "30%", color: "#fff", backgroundColor: "#004A89" }}>App Name</th>
                  <th style={{ color: "#fff", backgroundColor: "#004A89" }}>Keywords</th>
                  <th style={{ color: "#fff", backgroundColor: "#004A89" }}>Score</th>
                </tr>
              </thead>
              <tbody>
                {appsData.map((app) => (
                  <React.Fragment key={app.package_name}>
                    <tr>
                      <td rowSpan={app.keywords.length}>
                        <a
                          href={`https://play.google.com/store/apps/details?id=${app.package_name}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: "#004A89",
                            textDecoration: "none",
                            fontWeight: "bold",
                            fontSize: "0.8rem",
                          }}
                        >
                          {app.app_name}
                        </a>
                      </td>
                      <td>{app.keywords[0].keyword}</td>
                      <td>{app.keywords[0].score.toFixed(2)}</td>
                    </tr>
                    {app.keywords.slice(1).map((kw, index) => (
                      <tr key={index}>
                        <td>{kw.keyword}</td>
                        <td>{kw.score.toFixed(2)}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </Table>
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default KeywordRelevanceComponent;
